import type { FloraDefinition } from '@/flora/FloraDefinition';
import {
  FLORAL_MORPHOLOGIES,
  ecologyScore,
  floralMorphologyLabel,
  wildlifeGroupLabel,
  WILDLIFE_GROUPS,
  type FloralMorphology,
  type FoodResourceKind,
  type HabitatResourceKind,
  type WildlifeGroup,
} from '@/flora/FloraEcology';
import { resolveSeasonalAppearance } from '@/flora/FloraSeasonModel';
import { DAYS_IN_YEAR, dayOfYearFromDate } from '@/environment/SeasonModel';
import type { FloraResolver, ResolvedFlora } from '@/localization/FloraResolver';
import {
  MONTH_LABELS,
  buildWildlifeCalendar,
  type CalendarEntry,
  type CalendarRow,
} from '@/localization/Ecology';
import { windowContains } from '@/localization/Phenology';
import {
  maximumZoneOf,
  minimumZoneOf,
  nativeStatusLabel,
  primaryWarning,
  suitabilityBadge,
  type NativeStatus,
  type SuitabilityRating,
} from '@/localization/Suitability';
import { formatLength } from './dom';
import {
  buildFloraFactsheet,
  enumLabel,
  geometryFormLabel,
  numberLabel,
  percentLabel,
  type DisplayUnit,
  type Fact,
} from './FloraFactsheet';
import {
  categoryLabel,
  colorFamiliesOf,
  colorFamilyLabel,
  colorFamilyOf,
  formFactorLabel,
  formFactorOf,
  fullDisplayName,
  genusOf,
  speciesEpithetOf,
  type ColorFamily,
  type FormFactor,
} from './FloraFacets';
import { formatDayOfYear } from './PlantMetrics';
import type { SiteLocalization } from '@/localization/SiteLocalization';

/**
 * The Flora Database's browse model — the whole catalog, read six ways, with no DOM in sight.
 *
 * ## Why this file exists at all
 *
 * `FloraFacets` answers *which plants match*, and `FloraFactsheet` answers *everything about one
 * plant*. Neither answers the question the database is for: **how do these plants compare with each
 * other, and with this site?** That question is the whole of the module — every function here takes
 * a set of records and returns a shape a chart can be drawn from, so that the panel in
 * `FloraDatabasePanel.ts` is layout and nothing else.
 *
 * The same discipline the rest of the catalog UI follows applies here twice over, because a chart
 * lies more quietly than a table does:
 *
 * - **Nothing is computed twice.** Suitability comes from `FloraResolver`, months come from the same
 *   `resolveSeasonalAppearance` the canvas runs, the wildlife calendar comes from the same
 *   `buildWildlifeCalendar` the manifest's ecology tab uses. A bloom band on this screen falls in the
 *   month the renderer actually draws flowers, because it was read from the identical function.
 * - **Nothing is invented.** A field the record omits is reported as omitted — `absent` on a column,
 *   a greyed cell in the table, an unrecorded row in the wildlife lens — never defaulted into a
 *   plausible-looking value. Half the point of the database is showing where the catalog is thin, so
 *   that a suggestion can be sent about it.
 *
 * ## Colour
 *
 * Three colour jobs run in this module and they are kept strictly apart, because mixing them is how
 * a chart stops meaning anything:
 *
 * - **Literal** — bloom, foliage and fruit are drawn in the plant's own recorded hex. This is not a
 *   palette choice; a serviceberry's flowers are white because the record says `#f6f2e8`. Literal
 *   colour is why the Year lens works as a design instrument rather than as a chart.
 * - **Sequential** — magnitude (an ecology band, a value ranking) is one hue at varying strength,
 *   never a ramp of hues.
 * - **Status** — suitability is the HUD's existing good / warning / bad trio, reserved for state and
 *   always carrying its label in words, so the meaning never rests on colour alone.
 *
 * {@link COMPARE_COLORS} is the one categorical palette here, four fixed slots for the pinned plants
 * in the Compare lens; it is validated for both themes and assigned by pin order, never by rank.
 */

/* ---------------------------------------- Lenses ---------------------------------------- */

export type DatabaseLens = 'gallery' | 'table' | 'year' | 'site' | 'wildlife' | 'compare';

export interface LensOption {
  value: DatabaseLens;
  label: string;
  /** One line on what question this lens answers, shown as its tooltip. */
  hint: string;
  /** True where the lens is about the *site* rather than the plant, and says so without one. */
  needsSite?: boolean;
}

export const DATABASE_LENSES: readonly LensOption[] = [
  { value: 'gallery', label: 'Gallery', hint: 'Every plant as a plate — silhouette, colour year, size' },
  { value: 'table', label: 'Table', hint: 'The raw parameters, sortable, exportable' },
  { value: 'year', label: 'Year', hint: 'What each plant does month by month, in its own colours' },
  { value: 'site', label: 'Site', hint: 'What this climate will grow, and how big', needsSite: true },
  { value: 'wildlife', label: 'Wildlife', hint: 'What this planting palette feeds and shelters' },
  { value: 'compare', label: 'Compare', hint: 'Up to four plants, every parameter side by side' },
];

/** How many plants the Compare lens holds. Four columns is where a sheet stops being readable. */
export const COMPARE_LIMIT = 4;

/**
 * Identity colours for the pinned plants, in fixed slot order — never reassigned by rank, so a plant
 * that keeps its pin keeps its colour when another is unpinned.
 *
 * Both rows are validated for their own surface (lightness band, chroma floor, adjacent CVD
 * separation, normal-vision separation, contrast). The pair that fails first at four slots is gold
 * against red, so the fourth slot is pink rather than the red the HUD already spends on danger —
 * which keeps the categorical set clear of the status set as well.
 */
export const COMPARE_COLORS: Record<'light' | 'dark', readonly string[]> = {
  light: ['#3f8f45', '#4a7fd4', '#d9a12c', '#d1749c'],
  dark: ['#4fa24e', '#5288d4', '#b58527', '#cc6b8f'],
};

/* ----------------------------------------- Rows ----------------------------------------- */

export interface DatabaseRow {
  definition: FloraDefinition;
  /**
   * Read against the site — or against the catalog's reference climate when there is no site.
   * Always present, so no lens needs a second code path; `localized` says which it was.
   */
  resolved: ResolvedFlora;
  /** True when a real profile drove the resolution rather than the reference climate. */
  localized: boolean;
  id: string;
  /** Common name with cultivar. */
  name: string;
  botanicalName: string;
  genus: string;
  species: string;
  formFactor: FormFactor;
  colorFamilies: ColorFamily[];
  /**
   * Mature size *here* — the resolver's figure, not the record's typical. On an unlocalized site the
   * two are the same number, which is exactly the pre-localization behaviour.
   */
  heightMeters: number;
  spreadMeters: number;
  rating: SuitabilityRating;
  nativeStatus: NativeStatus;
  ecologyScore: number;
  /** True when the record carries an `ecology` block at all. */
  described: boolean;
  favorite: boolean;
}

export interface DatabaseContext {
  resolver: FloraResolver;
  favorites?: ReadonlySet<string>;
}

/** Read a set of records into database rows. Cheap — the resolver caches everything behind it. */
export function buildDatabaseRows(
  definitions: readonly FloraDefinition[],
  context: DatabaseContext,
): DatabaseRow[] {
  return definitions.map((definition) => {
    const resolved = context.resolver.resolve(definition);
    return {
      definition,
      resolved,
      localized: resolved.suitability.localized,
      id: definition.id,
      name: fullDisplayName(definition),
      botanicalName: definition.botanicalName,
      genus: genusOf(definition),
      species: speciesEpithetOf(definition),
      formFactor: formFactorOf(definition),
      colorFamilies: colorFamiliesOf(definition),
      heightMeters: resolved.growth.matureHeightMeters,
      spreadMeters: resolved.growth.matureSpreadMeters,
      rating: resolved.rating,
      nativeStatus: resolved.nativeStatus,
      ecologyScore: ecologyScore(definition.ecology),
      described: resolved.ecology.described,
      favorite: context.favorites?.has(definition.id) ?? false,
    };
  });
}

/* ---------------------------------------- The year --------------------------------------- */

export const MONTHS: readonly string[] = MONTH_LABELS;

/** Mid-month days of the year — the day each month's column is sampled at. */
export const MONTH_MID_DAYS: readonly number[] = MONTH_LABELS.map((_, index) =>
  dayOfYearFromDate(index + 1, 15),
);

/** One month of one plant's year, in the plant's own colours. */
export interface MonthFacet {
  month: number;
  foliageColor: string;
  /** 0–1. Zero is a bare deciduous plant, which the Year lens draws as a hairline rather than a gap. */
  foliageDensity: number;
  flowerColor: string | null;
  flowerDensity: number;
  fruitColor: string | null;
  fruitDensity: number;
  dormant: boolean;
}

/** The first fruit colour the record states in any season, or null where it names none. */
export function fruitColorOf(definition: FloraDefinition): string | null {
  for (const season of ['autumn', 'summer', 'winter', 'spring'] as const) {
    const color = definition.seasons[season].fruitColor;
    if (color !== undefined) return color;
  }
  return null;
}

/**
 * A plant's twelve months, sampled through the same appearance model the canvas runs.
 *
 * Fruit is the one figure that comes from two places, and deliberately: the seasonal blocks say what
 * colour the fruit is and roughly when it is dense enough to draw, while the *resolved* fruit window
 * knows when it actually ripens on this site — derived from this site's bloom plus the record's own
 * ripening and persistence figures. Taking the stronger of the two means a plant whose seasonal
 * blocks are coarse still fruits in the right month here, and one with no phenology figures still
 * fruits when its autumn block says it does.
 */
export function monthFacets(row: DatabaseRow): MonthFacet[] {
  const { seasons } = row.resolved.phenology;
  const fruitWindow = row.resolved.phenology.fruitWindow;
  const recordedFruit = fruitColorOf(row.definition);

  return MONTH_MID_DAYS.map((day, month) => {
    const state = resolveSeasonalAppearance(seasons, day);
    const inFruitWindow = fruitWindow !== undefined && windowContains(fruitWindow, day);
    const fruitDensity = Math.max(state.fruitDensity, inFruitWindow && recordedFruit ? 0.55 : 0);
    return {
      month,
      foliageColor: state.foliageColor,
      foliageDensity: state.foliageDensity,
      flowerColor: state.flowerDensity > 0.02 ? state.flowerColor : null,
      flowerDensity: state.flowerDensity,
      fruitColor: fruitDensity > 0.02 ? state.fruitColor ?? recordedFruit : null,
      fruitDensity,
      dormant: state.dormant,
    };
  });
}

/** A colour family's share of one month across the whole selection. */
export interface RiverBand {
  family: ColorFamily;
  label: string;
  /** Share of the month's total colour, in [0, 1]. The bands of a month sum to 1. */
  share: number;
  /** Plants contributing this family this month, for the tooltip. */
  contributors: number;
}

/**
 * The selection's year as a river of colour — twelve stacked months answering "what will this
 * palette actually look like in May".
 *
 * Foliage counts at a fraction of what bloom does. A hundred green leaves and one flower is not a
 * half-green half-pink month, and weighting them equally would flatten every month of every planting
 * into the same green band. The figure is a *reading of emphasis*, not a measurement of area, and it
 * is stated as such wherever it is drawn.
 */
export const BLOOM_EMPHASIS = 4;

export function paletteRiver(rows: readonly DatabaseRow[]): RiverBand[][] {
  const facetsByRow = rows.map((row) => monthFacets(row));

  return MONTH_LABELS.map((_, month) => {
    const weights = new Map<ColorFamily, { weight: number; contributors: number }>();
    const add = (family: ColorFamily, weight: number): void => {
      if (weight <= 0.01) return;
      const entry = weights.get(family) ?? { weight: 0, contributors: 0 };
      entry.weight += weight;
      entry.contributors += 1;
      weights.set(family, entry);
    };

    facetsByRow.forEach((facets, index) => {
      const facet = facets[month];
      const row = rows[index];
      if (!facet || !row) return;
      if (facet.foliageDensity > 0.05) add(colorFamilyOf(facet.foliageColor), facet.foliageDensity);
      if (facet.flowerColor && facet.flowerDensity > 0.02) {
        add(colorFamilyOf(facet.flowerColor), facet.flowerDensity * BLOOM_EMPHASIS);
      }
      if (facet.fruitColor && facet.fruitDensity > 0.02) {
        add(colorFamilyOf(facet.fruitColor), facet.fruitDensity * BLOOM_EMPHASIS);
      }
    });

    let total = 0;
    for (const entry of weights.values()) total += entry.weight;
    if (total <= 0) return [];

    return [...weights.entries()]
      .map(([family, entry]) => ({
        family,
        label: colorFamilyLabel(family),
        share: entry.weight / total,
        contributors: entry.contributors,
      }))
      .sort((a, b) => b.share - a.share);
  });
}

/* --------------------------------------- The site ---------------------------------------- */

/** The zone span a record claims, read exactly as `Suitability` reads it. */
export interface ZoneSpan {
  minimum: number;
  maximum: number;
}

/**
 * The plant's climatic range, or null where the record states neither end.
 *
 * Read through `Suitability`'s own accessors rather than off the record, so the bar drawn in the Site
 * lens is the same span the badge on the tile was judged against — including the conventions about
 * whole-zone records meaning the whole zone.
 */
export function zoneSpanOf(definition: FloraDefinition): ZoneSpan | null {
  const minimum = minimumZoneOf(definition);
  const maximum = maximumZoneOf(definition);
  if (minimum === undefined && maximum === undefined) return null;
  return {
    minimum: minimum ?? ZONE_AXIS.minimum,
    maximum: maximum ?? ZONE_AXIS.maximum,
  };
}

/** The hardiness axis the Site lens plots against — the USDA range the catalog lives inside. */
export const ZONE_AXIS = { minimum: 2, maximum: 11 } as const;

export interface SuitabilityBand {
  rating: SuitabilityRating;
  label: string;
  count: number;
  /** Share of the selection, in [0, 1]. */
  share: number;
  /** `good` / `warn` / `bad`, matching the HUD's existing status tones. */
  tone: 'good' | 'neutral' | 'warn' | 'bad';
}

const RATING_TONES: Record<SuitabilityRating, SuitabilityBand['tone']> = {
  ideal: 'good',
  // Deliberately not a colour. `suitable` is the unremarkable answer, and giving it a hue of its own
  // would put four colours on a scale where only three of them are telling you anything.
  suitable: 'neutral',
  marginal: 'warn',
  'outside-range': 'bad',
};

export function suitabilityBands(rows: readonly DatabaseRow[]): SuitabilityBand[] {
  const order: SuitabilityRating[] = ['ideal', 'suitable', 'marginal', 'outside-range'];
  const total = rows.length || 1;
  return order.map((rating) => {
    const count = rows.filter((row) => row.rating === rating).length;
    return {
      rating,
      label: suitabilityBadge(rating),
      count,
      share: count / total,
      tone: RATING_TONES[rating],
    };
  });
}

/** A point on the Site lens's size plot. Shape carries the form factor; colour carries nothing. */
export interface ScalePoint {
  row: DatabaseRow;
  heightMeters: number;
  spreadMeters: number;
}

export function scalePoints(rows: readonly DatabaseRow[]): ScalePoint[] {
  return rows.map((row) => ({
    row,
    heightMeters: row.heightMeters,
    spreadMeters: row.spreadMeters,
  }));
}

/* -------------------------------------- Wildlife ---------------------------------------- */

export interface GroupTally {
  group: WildlifeGroup;
  label: string;
  /** Plants in the selection that offer this group anything. */
  count: number;
  share: number;
  /** The strongest few, by ecological value. */
  top: DatabaseRow[];
}

export interface HostEntry {
  row: DatabaseRow;
  group: WildlifeGroup;
  label: string;
  speciesCount: number | null;
  note: string | null;
}

export interface MorphologyTally {
  /** Null is the "not recorded yet" bucket, which is a finding rather than a rounding error. */
  morphology: FloralMorphology | null;
  label: string;
  count: number;
}

export interface EcologyRollup {
  food: CalendarRow<FoodResourceKind>[];
  habitat: CalendarRow<HabitatResourceKind>[];
  groups: GroupTally[];
  /** The selection ranked by ecological value, strongest first. */
  ranked: DatabaseRow[];
  keystones: DatabaseRow[];
  hosts: HostEntry[];
  specialists: HostEntry[];
  /** Records carrying an ecology block, out of the selection. */
  described: number;
  total: number;
  morphology: MorphologyTally[];
}

/**
 * Scale each calendar row to its own strongest month.
 *
 * The shared {@link buildWildlifeCalendar} saturates against an **absolute** scale, which is exactly
 * right for the manifest: a garden should not look worse for gaining a shrub, so the bands only ever
 * grow when the planting does. Point that same scale at a fifty-plant *catalog* and every habitat row
 * pins at full strength in all twelve months — a solid block that answers nothing.
 *
 * The two surfaces are asking different questions. The manifest asks *how much is there*; a shortlist
 * asks *when does this happen, and is there a hole in it*. So the figures still come from the one
 * shared calculation — nothing is recomputed, and the months are the same months the manifest and the
 * renderer use — and only the display scale changes, per row, with the number of contributing plants
 * carried alongside so the magnitude is never lost.
 */
export function normalizeCalendar<Kind extends string>(
  rows: readonly CalendarRow<Kind>[],
): CalendarRow<Kind>[] {
  return rows.map((row) => {
    const peak = Math.max(...row.months, 0);
    return peak <= 0 ? row : { ...row, months: row.months.map((value) => value / peak) };
  });
}

/**
 * The selection read as a food and habitat calendar.
 *
 * Every plant counts once, which is the honest reading for a *catalog*: the bands answer "what would
 * this shortlist offer through the year, one of each" rather than pretending to know quantities
 * nobody has placed yet. The manifest's ecology tab asks the same function the other question — what
 * is actually planted, at the counts it is planted in — and the two agree because they are one
 * function. See {@link normalizeCalendar} for the one thing that differs, and why.
 */
export function ecologyRollup(
  rows: readonly DatabaseRow[],
  localization: SiteLocalization,
): EcologyRollup {
  const entries: CalendarEntry[] = rows.map((row) => ({
    definition: row.definition,
    ecology: row.resolved.ecology,
    count: 1,
  }));
  const calendar = entries.length > 0 ? buildWildlifeCalendar(entries, localization) : null;

  const total = rows.length;
  const ranked = [...rows].sort((a, b) => b.ecologyScore - a.ecologyScore);

  const groups: GroupTally[] = WILDLIFE_GROUPS.map((group) => {
    const serving = rows.filter((row) => row.resolved.ecology.groups.includes(group));
    return {
      group,
      label: wildlifeGroupLabel(group),
      count: serving.length,
      share: total === 0 ? 0 : serving.length / total,
      top: [...serving].sort((a, b) => b.ecologyScore - a.ecologyScore).slice(0, 3),
    };
  }).filter((tally) => tally.count > 0);

  const hosts: HostEntry[] = [];
  const specialists: HostEntry[] = [];
  for (const row of rows) {
    for (const relation of row.resolved.ecology.larvalHosts) {
      hosts.push({
        row,
        group: relation.group,
        label: wildlifeGroupLabel(relation.group),
        speciesCount: relation.speciesCount ?? null,
        note: relation.note ?? null,
      });
    }
    for (const relation of row.definition.ecology?.specialists ?? []) {
      specialists.push({
        row,
        group: relation.group,
        label: `${wildlifeGroupLabel(relation.group)} · ${relation.resource}`,
        speciesCount: relation.speciesCount ?? null,
        note: relation.note ?? null,
      });
    }
  }
  hosts.sort((a, b) => (b.speciesCount ?? 0) - (a.speciesCount ?? 0));

  const morphology: MorphologyTally[] = [
    ...FLORAL_MORPHOLOGIES.map<MorphologyTally>((value) => ({
      morphology: value,
      label: floralMorphologyLabel(value),
      count: rows.filter((row) => row.definition.ecology?.floralMorphology === value).length,
    })),
    {
      morphology: null,
      label: 'Not recorded',
      count: rows.filter((row) => row.definition.ecology?.floralMorphology === undefined).length,
    },
  ].filter((entry) => entry.count > 0);

  return {
    food: normalizeCalendar(calendar?.food ?? []),
    habitat: normalizeCalendar(calendar?.habitat ?? []),
    groups,
    ranked,
    keystones: rows.filter((row) => row.resolved.ecology.keystone),
    hosts,
    specialists,
    described: rows.filter((row) => row.described).length,
    total,
    morphology,
  };
}

/* ------------------------------------ Table columns -------------------------------------- */

export type TableGroupId =
  | 'identity'
  | 'size'
  | 'climate'
  | 'season'
  | 'ecology'
  | 'placement'
  | 'rendering';

export interface TableGroup {
  id: TableGroupId;
  label: string;
  hint: string;
  /** Shown by default. The table opens on what a designer browses, not on everything at once. */
  defaultOn: boolean;
}

export const TABLE_GROUPS: readonly TableGroup[] = [
  { id: 'identity', label: 'Identity', hint: 'Names, category, and what it needs to live', defaultOn: true },
  { id: 'size', label: 'Size', hint: 'Mature dimensions, growth rate, and the curves behind them', defaultOn: true },
  { id: 'climate', label: 'Climate', hint: 'Hardiness, heat, season length, and this site’s verdict', defaultOn: true },
  { id: 'season', label: 'Season', hint: 'Leaf-out, bloom, colour, and drop', defaultOn: false },
  { id: 'ecology', label: 'Ecology', hint: 'What it feeds and shelters', defaultOn: false },
  { id: 'placement', label: 'Placement', hint: 'Footprint, spacing, and slope limits', defaultOn: false },
  { id: 'rendering', label: 'Rendering', hint: 'Geometry archetype and level-of-detail distances', defaultOn: false },
];

export interface TableColumn {
  id: string;
  group: TableGroupId;
  label: string;
  /** Right-aligned and tabular. */
  numeric: boolean;
  /**
   * Dotted path to the field in the catalog record, for a parameter-level suggestion. Null on derived
   * columns — you cannot propose a new value for a figure nobody stored.
   */
  field: string | null;
  value: (row: DatabaseRow, unit: DisplayUnit) => string;
  sortKey: (row: DatabaseRow) => number | string;
  /** True where the record leaves this field out, so the cell can be drawn as unset. */
  absent?: (row: DatabaseRow) => boolean;
  hint?: string;
}

const EM_DASH = '—';

const RATING_ORDER: Record<SuitabilityRating, number> = {
  ideal: 0,
  suitable: 1,
  marginal: 2,
  'outside-range': 3,
};

export const TABLE_COLUMNS: readonly TableColumn[] = [
  {
    id: 'name',
    group: 'identity',
    label: 'Common name',
    numeric: false,
    field: 'commonNames',
    value: (row) => row.name,
    sortKey: (row) => row.name.toLowerCase(),
  },
  {
    id: 'botanical',
    group: 'identity',
    label: 'Botanical name',
    numeric: false,
    field: 'botanicalName',
    value: (row) => row.botanicalName,
    sortKey: (row) => row.botanicalName.toLowerCase(),
  },
  {
    id: 'category',
    group: 'identity',
    label: 'Category',
    numeric: false,
    field: 'category',
    value: (row) => categoryLabel(row.definition.category),
    sortKey: (row) => row.definition.category,
  },
  {
    id: 'form',
    group: 'identity',
    label: 'Form',
    numeric: false,
    field: null,
    value: (row) => formFactorLabel(row.formFactor),
    sortKey: (row) => row.formFactor,
    hint: 'Derived from the category — the palette’s primary axis',
  },
  {
    id: 'sunlight',
    group: 'identity',
    label: 'Sun',
    numeric: false,
    field: 'sunlight',
    value: (row) => row.definition.sunlight.map(enumLabel).join(' · ') || EM_DASH,
    sortKey: (row) => row.definition.sunlight[0] ?? '',
    absent: (row) => row.definition.sunlight.length === 0,
  },
  {
    id: 'moisture',
    group: 'identity',
    label: 'Moisture',
    numeric: false,
    field: 'moisture',
    value: (row) => row.definition.moisture.map(enumLabel).join(' · ') || EM_DASH,
    sortKey: (row) => row.definition.moisture[0] ?? '',
    absent: (row) => row.definition.moisture.length === 0,
  },
  {
    id: 'tags',
    group: 'identity',
    label: 'Traits',
    numeric: false,
    field: 'tags',
    value: (row) => row.definition.tags.map((tag) => tag.replace(/-/g, ' ')).join(', ') || EM_DASH,
    sortKey: (row) => row.definition.tags.length,
    absent: (row) => row.definition.tags.length === 0,
  },

  {
    id: 'height',
    group: 'size',
    label: 'Height',
    numeric: true,
    field: 'growth.matureHeightMeters',
    value: (row, unit) => formatLength(row.heightMeters, unit),
    sortKey: (row) => row.heightMeters,
    hint: 'Expected here — a point inside the record’s own mature range',
  },
  {
    id: 'spread',
    group: 'size',
    label: 'Spread',
    numeric: true,
    field: 'growth.matureSpreadMeters',
    value: (row, unit) => formatLength(row.spreadMeters, unit),
    sortKey: (row) => row.spreadMeters,
  },
  {
    id: 'height-range',
    group: 'size',
    label: 'Height range',
    numeric: false,
    field: 'growth.matureHeightMeters',
    value: (row, unit) => {
      const height = row.definition.growth.matureHeightMeters;
      return `${formatLength(height.minimum, unit)} – ${formatLength(height.maximum, unit)}`;
    },
    sortKey: (row) =>
      row.definition.growth.matureHeightMeters.maximum -
      row.definition.growth.matureHeightMeters.minimum,
  },
  {
    id: 'growth-rate',
    group: 'size',
    label: 'Growth / yr',
    numeric: true,
    field: 'growth.annualGrowthRateMeters',
    value: (row, unit) => {
      const rate = row.definition.growth.annualGrowthRateMeters;
      return rate === undefined ? EM_DASH : formatLength(rate, unit);
    },
    sortKey: (row) => row.definition.growth.annualGrowthRateMeters ?? -1,
    absent: (row) => row.definition.growth.annualGrowthRateMeters === undefined,
  },
  {
    id: 'height-curve',
    group: 'size',
    label: 'Height curve',
    numeric: false,
    field: 'growth.heightCurve',
    value: (row) => enumLabel(row.definition.growth.heightCurve),
    sortKey: (row) => row.definition.growth.heightCurve,
  },
  {
    id: 'stages',
    group: 'size',
    label: 'Stages',
    numeric: true,
    field: 'growth.containerStages',
    value: (row) => String(row.definition.growth.containerStages.length),
    sortKey: (row) => row.definition.growth.containerStages.length,
    hint: 'Container sizes this plant can be specified and priced at',
  },

  {
    id: 'suitability',
    group: 'climate',
    label: 'This site',
    numeric: false,
    field: null,
    value: (row) => (row.localized ? suitabilityBadge(row.rating) : EM_DASH),
    sortKey: (row) => RATING_ORDER[row.rating],
    absent: (row) => !row.localized,
    hint: 'Derived from this site’s climate — never stored',
  },
  {
    id: 'native',
    group: 'climate',
    label: 'Regional status',
    numeric: false,
    field: 'nativeRange',
    value: (row) => (row.localized ? nativeStatusLabel(row.nativeStatus) : EM_DASH),
    sortKey: (row) => row.nativeStatus,
    absent: (row) => !row.localized || row.nativeStatus === 'unknown',
  },
  {
    id: 'zones',
    group: 'climate',
    label: 'Zones',
    numeric: false,
    field: 'hardinessZones',
    value: (row) => {
      const span = zoneSpanOf(row.definition);
      return span === null ? EM_DASH : `${numberLabel(span.minimum, 1)} – ${numberLabel(span.maximum, 1)}`;
    },
    sortKey: (row) => zoneSpanOf(row.definition)?.minimum ?? 99,
    absent: (row) => zoneSpanOf(row.definition) === null,
  },
  {
    id: 'headroom',
    group: 'climate',
    label: 'Winter margin',
    numeric: true,
    field: null,
    value: (row) =>
      row.localized && Number.isFinite(row.resolved.suitability.coldHeadroom)
        ? `${numberLabel(row.resolved.suitability.coldHeadroom, 1)} zones`
        : EM_DASH,
    sortKey: (row) =>
      Number.isFinite(row.resolved.suitability.coldHeadroom)
        ? row.resolved.suitability.coldHeadroom
        : 99,
    absent: (row) => !row.localized || !Number.isFinite(row.resolved.suitability.coldHeadroom),
    hint: 'How far this site sits above the coldest winter the plant survives',
  },
  {
    id: 'heat',
    group: 'climate',
    label: 'Heat zone',
    numeric: true,
    field: 'climate.maximumHeatZone',
    value: (row) => {
      const heat = row.definition.climate?.maximumHeatZone;
      return heat === undefined ? EM_DASH : String(heat);
    },
    sortKey: (row) => row.definition.climate?.maximumHeatZone ?? 99,
    absent: (row) => row.definition.climate?.maximumHeatZone === undefined,
  },
  {
    id: 'season-days',
    group: 'climate',
    label: 'Season needed',
    numeric: true,
    field: 'climate.minimumGrowingSeasonDays',
    value: (row) => {
      const days = row.definition.climate?.minimumGrowingSeasonDays;
      return days === undefined ? EM_DASH : `${days} d`;
    },
    sortKey: (row) => row.definition.climate?.minimumGrowingSeasonDays ?? -1,
    absent: (row) => row.definition.climate?.minimumGrowingSeasonDays === undefined,
  },
  {
    id: 'warning',
    group: 'climate',
    label: 'Concern',
    numeric: false,
    field: null,
    value: (row) => primaryWarning(row.resolved.suitability)?.headline ?? EM_DASH,
    sortKey: (row) => primaryWarning(row.resolved.suitability)?.headline ?? '',
    absent: (row) => primaryWarning(row.resolved.suitability) === undefined,
  },

  {
    id: 'evergreen',
    group: 'season',
    label: 'Evergreen',
    numeric: false,
    field: 'seasons.evergreen',
    value: (row) => (row.definition.seasons.evergreen ? 'Yes' : 'No'),
    sortKey: (row) => (row.definition.seasons.evergreen ? 0 : 1),
  },
  {
    id: 'leaf-out',
    group: 'season',
    label: 'Leaf out',
    numeric: false,
    field: 'seasons.leafOutDay',
    value: (row) => dayOrDash(row.resolved.phenology.leafOutDay),
    sortKey: (row) => row.resolved.phenology.leafOutDay ?? 999,
    absent: (row) => row.resolved.phenology.leafOutDay === undefined,
    hint: 'Moved to this site’s spring',
  },
  {
    id: 'bloom',
    group: 'season',
    label: 'Bloom',
    numeric: false,
    field: 'seasons.bloomWindows',
    value: (row) => {
      const first = row.resolved.phenology.bloomWindows[0];
      if (!first) return EM_DASH;
      return `${formatDayOfYear(first.startDayOfYear)} – ${formatDayOfYear(first.endDayOfYear)}`;
    },
    sortKey: (row) => row.resolved.phenology.bloomWindows[0]?.startDayOfYear ?? 999,
    absent: (row) => row.resolved.phenology.bloomWindows.length === 0,
  },
  {
    id: 'fruit',
    group: 'season',
    label: 'Fruit',
    numeric: false,
    field: 'phenology.fruitMaturationDays',
    value: (row) => {
      const window = row.resolved.phenology.fruitWindow;
      if (!window) return EM_DASH;
      return `${formatDayOfYear(window.startDay)} – ${formatDayOfYear(window.endDay)}`;
    },
    sortKey: (row) => row.resolved.phenology.fruitWindow?.startDay ?? 999,
    absent: (row) => row.resolved.phenology.fruitWindow === undefined,
  },
  {
    id: 'autumn',
    group: 'season',
    label: 'Autumn colour',
    numeric: false,
    field: 'seasons.autumnColorStartDay',
    value: (row) => dayOrDash(row.resolved.phenology.autumnColorStartDay),
    sortKey: (row) => row.resolved.phenology.autumnColorStartDay ?? 999,
    absent: (row) => row.resolved.phenology.autumnColorStartDay === undefined,
  },
  {
    id: 'leaf-drop',
    group: 'season',
    label: 'Leaf drop',
    numeric: false,
    field: 'seasons.leafDropDay',
    value: (row) => dayOrDash(row.resolved.phenology.leafDropDay),
    sortKey: (row) => row.resolved.phenology.leafDropDay ?? 999,
    absent: (row) => row.resolved.phenology.leafDropDay === undefined,
  },
  {
    id: 'in-leaf',
    group: 'season',
    label: 'Days in leaf',
    numeric: true,
    field: null,
    value: (row) => `${daysInLeaf(row)} d`,
    sortKey: (row) => daysInLeaf(row),
    hint: 'How much of this site’s year the plant is actually clothed',
  },

  {
    id: 'eco-value',
    group: 'ecology',
    label: 'Ecological value',
    numeric: true,
    field: 'ecology',
    value: (row) => (row.described ? percentLabel(row.ecologyScore) : EM_DASH),
    sortKey: (row) => row.ecologyScore,
    absent: (row) => !row.described,
    hint: 'Food weighted over habitat, hosting over both — for ranking, not for citing',
  },
  {
    id: 'groups',
    group: 'ecology',
    label: 'Serves',
    numeric: false,
    field: 'ecology',
    value: (row) =>
      row.resolved.ecology.groups.map(wildlifeGroupLabel).join(', ') || EM_DASH,
    sortKey: (row) => row.resolved.ecology.groups.length,
    absent: (row) => row.resolved.ecology.groups.length === 0,
  },
  {
    id: 'host',
    group: 'ecology',
    label: 'Larval host',
    numeric: true,
    field: 'ecology.larvalHosts',
    value: (row) => {
      const hosts = row.resolved.ecology.larvalHosts;
      if (hosts.length === 0) return EM_DASH;
      const counted = hosts.reduce((total, host) => total + (host.speciesCount ?? 0), 0);
      return counted > 0 ? `${counted} species` : 'Documented';
    },
    sortKey: (row) =>
      row.resolved.ecology.larvalHosts.reduce((total, host) => total + (host.speciesCount ?? 0), 0),
    absent: (row) => row.resolved.ecology.larvalHosts.length === 0,
  },
  {
    id: 'morphology',
    group: 'ecology',
    label: 'Flower shape',
    numeric: false,
    field: 'ecology.floralMorphology',
    value: (row) => {
      const morphology = row.definition.ecology?.floralMorphology;
      return morphology === undefined ? EM_DASH : floralMorphologyLabel(morphology);
    },
    sortKey: (row) => row.definition.ecology?.floralMorphology ?? 'zzz',
    absent: (row) => row.definition.ecology?.floralMorphology === undefined,
    hint: 'Decides which pollinators can reach the nectar at all',
  },
  {
    id: 'keystone',
    group: 'ecology',
    label: 'Keystone',
    numeric: false,
    field: 'ecology.keystone',
    value: (row) => (row.resolved.ecology.keystone ? 'Yes' : EM_DASH),
    sortKey: (row) => (row.resolved.ecology.keystone ? 0 : 1),
    absent: (row) => !row.resolved.ecology.keystone,
  },
  {
    id: 'deer',
    group: 'ecology',
    label: 'Deer resistant',
    numeric: false,
    field: 'ecology.deerResistant',
    value: (row) => (row.definition.ecology?.deerResistant === true ? 'Yes' : EM_DASH),
    sortKey: (row) => (row.definition.ecology?.deerResistant === true ? 0 : 1),
    absent: (row) => row.definition.ecology?.deerResistant !== true,
  },

  {
    id: 'footprint',
    group: 'placement',
    label: 'Footprint radius',
    numeric: true,
    field: 'placement.footprintRadiusMeters',
    value: (row, unit) => formatLength(row.definition.placement.footprintRadiusMeters, unit),
    sortKey: (row) => row.definition.placement.footprintRadiusMeters,
  },
  {
    id: 'spacing',
    group: 'placement',
    label: 'Spacing',
    numeric: true,
    field: 'placement.recommendedSpacingMeters',
    value: (row, unit) => formatLength(row.definition.placement.recommendedSpacingMeters, unit),
    sortKey: (row) => row.definition.placement.recommendedSpacingMeters,
    hint: 'Centre to centre, at maturity',
  },
  {
    id: 'slope',
    group: 'placement',
    label: 'Max slope',
    numeric: true,
    field: 'placement.maxSlopeDegrees',
    value: (row) => `${numberLabel(row.definition.placement.maxSlopeDegrees, 1)}°`,
    sortKey: (row) => row.definition.placement.maxSlopeDegrees,
  },

  {
    id: 'geometry',
    group: 'rendering',
    label: 'Geometry',
    numeric: false,
    field: 'rendering.geometryForm',
    value: (row) => geometryFormLabel(row.definition.rendering.geometryForm),
    sortKey: (row) => row.definition.rendering.geometryForm,
  },
  {
    id: 'lod',
    group: 'rendering',
    label: 'LOD',
    numeric: false,
    field: 'rendering.lodDistancesMeters',
    value: (row, unit) => {
      const [medium, low] = row.definition.rendering.lodDistancesMeters;
      return `${formatLength(medium, unit)} / ${formatLength(low, unit)}`;
    },
    sortKey: (row) => row.definition.rendering.lodDistancesMeters[0],
  },
  {
    id: 'impostor',
    group: 'rendering',
    label: 'Impostor from',
    numeric: true,
    field: 'rendering.impostorDistanceMeters',
    value: (row, unit) => formatLength(row.definition.rendering.impostorDistanceMeters, unit),
    sortKey: (row) => row.definition.rendering.impostorDistanceMeters,
  },
  {
    id: 'catalog-id',
    group: 'rendering',
    label: 'Catalog id',
    numeric: false,
    field: 'id',
    value: (row) => row.id,
    sortKey: (row) => row.id,
  },
];

function dayOrDash(day: number | undefined): string {
  return day === undefined ? EM_DASH : formatDayOfYear(day);
}

/** How many days of this site's year the plant carries leaves. */
export function daysInLeaf(row: DatabaseRow): number {
  const { foliageWindow } = row.resolved.phenology;
  const span = foliageWindow.endDay - foliageWindow.startDay;
  return Math.round(span >= 0 ? span : span + DAYS_IN_YEAR);
}

export function columnsFor(groups: readonly TableGroupId[]): TableColumn[] {
  return TABLE_COLUMNS.filter((column) => groups.includes(column.group));
}

export type SortDirection = 'ascending' | 'descending';

/** Sort rows by a column. Stable by name, so equal keys never shuffle between renders. */
export function sortRows(
  rows: readonly DatabaseRow[],
  columnId: string,
  direction: SortDirection,
): DatabaseRow[] {
  const column = TABLE_COLUMNS.find((entry) => entry.id === columnId);
  const sign = direction === 'ascending' ? 1 : -1;
  const sorted = [...rows];
  sorted.sort((a, b) => {
    if (column) {
      const ka = column.sortKey(a);
      const kb = column.sortKey(b);
      if (ka !== kb) {
        const order =
          typeof ka === 'number' && typeof kb === 'number'
            ? ka - kb
            : String(ka).localeCompare(String(kb));
        return order * sign;
      }
    }
    return a.name.localeCompare(b.name);
  });
  return sorted;
}

/** Whatever the table is showing, as CSV — you export exactly what you are reading. */
export function databaseCsv(
  rows: readonly DatabaseRow[],
  columns: readonly TableColumn[],
  unit: DisplayUnit,
): string {
  const escape = (value: string): string =>
    /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  const lines = [columns.map((column) => escape(column.label)).join(',')];
  for (const row of rows) {
    lines.push(columns.map((column) => escape(column.value(row, unit))).join(','));
  }
  return `${lines.join('\n')}\n`;
}

/* --------------------------------------- Compare ----------------------------------------- */

export interface CompareCell {
  value: string;
  absent: boolean;
  color: string | null;
  meter: number | null;
}

export interface CompareRow {
  label: string;
  hint: string | null;
  cells: CompareCell[];
  /** True when the pinned plants do not all say the same thing — the reason to look at the row. */
  differs: boolean;
}

export interface CompareSection {
  id: string;
  title: string;
  note: string;
  rows: CompareRow[];
}

/**
 * The pinned plants, every parameter, side by side.
 *
 * Built by running each plant through the very same {@link buildFloraFactsheet} the record card
 * draws and transposing the result. That is the whole trick, and it is why this lens cannot fall
 * behind the schema: a field added to the factsheet appears as a row here on the same build, and a
 * figure shown here is the figure the card shows, character for character.
 */
export function compareSections(
  rows: readonly DatabaseRow[],
  unit: DisplayUnit,
): CompareSection[] {
  if (rows.length === 0) return [];
  const sheets = rows.map((row) =>
    buildFloraFactsheet(row.definition, unit, row.localized ? row.resolved : undefined),
  );

  // Section order follows the first pinned plant, then anything only later ones carry — so pinning a
  // plant with an ecology block adds the block rather than reordering the sheet you were reading.
  const sectionIds: string[] = [];
  for (const sheet of sheets) {
    for (const section of sheet.sections) {
      if (!sectionIds.includes(section.id)) sectionIds.push(section.id);
    }
  }

  return sectionIds.map((id) => {
    const present = sheets
      .map((sheet) => sheet.sections.find((section) => section.id === id))
      .filter((section): section is NonNullable<typeof section> => section !== undefined);
    const first = present[0];

    const labels: string[] = [];
    for (const section of present) {
      for (const item of section.facts) if (!labels.includes(item.label)) labels.push(item.label);
    }

    const compareRows: CompareRow[] = labels.map((label) => {
      const cells = sheets.map((sheet) => {
        const section = sheet.sections.find((entry) => entry.id === id);
        const item = section?.facts.find((entry) => entry.label === label);
        return cellFor(item);
      });
      const shown = cells.map((cell) => (cell.absent ? '' : cell.value));
      return {
        label,
        hint: present.flatMap((s) => s.facts).find((f) => f.label === label)?.hint ?? null,
        cells,
        differs: new Set(shown).size > 1,
      };
    });

    return {
      id,
      title: first?.title ?? id,
      note: first?.note ?? '',
      rows: compareRows,
    };
  });
}

function cellFor(item: Fact | undefined): CompareCell {
  if (!item) return { value: EM_DASH, absent: true, color: null, meter: null };
  return { value: item.value, absent: item.absent, color: item.color, meter: item.meter };
}

/* ---------------------------------------- Summary ---------------------------------------- */

/** `38 of 54 plants · 11 ideal here · 6 keystone` — the footer line under every lens. */
export function databaseSummary(
  rows: readonly DatabaseRow[],
  total: number,
  localized: boolean,
): string {
  const parts = [`${rows.length} of ${total} plant${total === 1 ? '' : 's'}`];
  if (localized && rows.length > 0) {
    const fits = rows.filter((row) => row.rating === 'ideal' || row.rating === 'suitable').length;
    parts.push(`${fits} suit this site`);
  }
  const described = rows.filter((row) => row.described).length;
  if (described > 0) parts.push(`${described} carry ecology data`);
  return parts.join('  ·  ');
}
